/**
 * The status chip (Live / Off / Never published) and the on/off switch
 * (spec 2026-10-23 §5). The switch never flips by itself: a click opens the
 * confirm dialog. When it cannot be used, the reason is written under it.
 */
import { cn } from '@/lib/utils';
import type { IntroStatus, SwitchState } from '../lib/intro-state';

const CHIP: Record<IntroStatus, { label: string; className: string }> = {
  live: {
    label: 'Live',
    className: 'border-green-300 bg-green-100 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-300',
  },
  off: {
    label: 'Off',
    className: 'border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300',
  },
  never: { label: 'Never published', className: 'border-border bg-muted text-muted-foreground' },
};

export function StatusChip({ status }: { status: IntroStatus }) {
  const chip = CHIP[status];
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium', chip.className)}>
      {chip.label}
    </span>
  );
}

interface Props {
  /** What the switch reads. The intro and the Abby welcome tab both give it. */
  sw: Pick<SwitchState, 'on' | 'canToggle' | 'reason'>;
  disabled: boolean;
  onToggle: () => void;
  /** The switch's name. The default is the intro's. */
  label?: string;
  /** Start of the element ids; each tab needs its own. The default keeps the intro tab's ids. */
  idPrefix?: string;
}

export function IntroSwitch({ sw, disabled, onToggle, label = 'Show the intro to new installs', idPrefix = 'intro' }: Props) {
  const off = disabled || !sw.canToggle;
  const labelId = `${idPrefix}-switch-label`;
  const reasonId = `${idPrefix}-switch-reason`;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3">
        <span id={labelId} className="text-sm font-medium">
          {label}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={sw.on}
          aria-labelledby={labelId}
          aria-describedby={sw.reason ? reasonId : undefined}
          disabled={off}
          onClick={onToggle}
          className={cn(
            'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            sw.on ? 'bg-green-600' : 'bg-slate-400 dark:bg-slate-600',
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              'inline-block h-5 w-5 rounded-full bg-white shadow transition-transform',
              sw.on ? 'translate-x-5' : 'translate-x-0',
            )}
          />
        </button>
        <span className="text-sm text-muted-foreground" aria-hidden="true">
          {sw.on ? 'On' : 'Off'}
        </span>
      </div>
      {sw.reason && (
        <p id={reasonId} className="text-xs text-muted-foreground">
          {sw.reason}
        </p>
      )}
    </div>
  );
}
